import { useRef, useState, useCallback } from "react";

/**
 * Shared Web Audio API hook for Dignity narration playback.
 * Provides AudioContext singleton, dual-level caching (ArrayBuffer → AudioBuffer),
 * play/stop/volume controls, and preloading.
 *
 * Used by both ConsumerHomepage (intro narration) and DignityNarrationPlayer (per-page narration).
 */
export function useDignityAudio(initialVolume = 0.5) {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const arrayBufferCacheRef = useRef<Map<string, ArrayBuffer>>(new Map());
  const audioBufferCacheRef = useRef<Map<string, AudioBuffer>>(new Map());
  const [volume, setVolumeState] = useState(initialVolume);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  // Store volume in a ref so getAudioCtx always has the latest value
  const volumeRef = useRef(initialVolume);
  // Cancellation token — incremented on every stopAudio call so in-flight playAudioSrc calls abort
  const playGenRef = useRef(0);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      gainNodeRef.current = audioCtxRef.current.createGain();
      gainNodeRef.current.gain.value = volumeRef.current;
      gainNodeRef.current.connect(audioCtxRef.current.destination);
    }
    return audioCtxRef.current;
  }, []);

  /** Stop playback completely (destroys source node). */
  const stopAudio = useCallback(() => {
    playGenRef.current++;  // invalidate any in-flight playAudioSrc calls
    if (audioSourceRef.current) {
      try { audioSourceRef.current.stop(); } catch (_) {}
      audioSourceRef.current = null;
    }
    // Resume context if it was suspended (so future plays work)
    if (audioCtxRef.current?.state === "suspended") {
      audioCtxRef.current.resume();
    }
    setIsPlaying(false);
    setIsPaused(false);
  }, []);

  /** Pause playback (suspends AudioContext, preserving position). */
  const pauseAudio = useCallback(() => {
    if (audioCtxRef.current && audioCtxRef.current.state === "running" && audioSourceRef.current) {
      audioCtxRef.current.suspend();
      setIsPaused(true);
      setIsPlaying(false);
    }
  }, []);

  /** Resume playback from where it was paused. */
  const resumeAudio = useCallback(() => {
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended" && audioSourceRef.current) {
      audioCtxRef.current.resume();
      setIsPaused(false);
      setIsPlaying(true);
    }
  }, []);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    volumeRef.current = v;
    if (gainNodeRef.current) gainNodeRef.current.gain.value = v;
  }, []);

  /**
   * Play an audio source URL. Returns duration in seconds, or null on error.
   * Uses dual-level cache: decoded AudioBuffer (instant) → raw ArrayBuffer (decode only) → fetch.
   */
  const playAudioSrc = useCallback((src: string, onEnded: () => void): Promise<number | null> => {
    const gen = ++playGenRef.current;  // claim a generation token
    return new Promise((resolve) => {
      const ctx = getAudioCtx();
      ctx.resume().then(async () => {
        try {
          // Fast path: already decoded AudioBuffer
          if (audioBufferCacheRef.current.has(src)) {
            if (gen !== playGenRef.current) { resolve(null); return; }  // cancelled
            const decoded = audioBufferCacheRef.current.get(src)!;
            stopAudio();
            playGenRef.current = gen;  // restore our token after stopAudio incremented it
            setIsPlaying(true);
            const source = ctx.createBufferSource();
            source.buffer = decoded;
            source.connect(gainNodeRef.current ?? ctx.destination);
            source.onended = () => { if (gen === playGenRef.current) { setIsPlaying(false); onEnded(); } };
            source.start(0);
            audioSourceRef.current = source;
            resolve(decoded.duration);
            return;
          }

          // Medium path: raw bytes already prefetched, just decode
          let raw: ArrayBuffer;
          if (arrayBufferCacheRef.current.has(src)) {
            raw = arrayBufferCacheRef.current.get(src)!.slice(0); // slice — decodeAudioData detaches
          } else {
            const r = await fetch(src, { cache: "no-store" });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            raw = await r.arrayBuffer();
          }

          // Check if cancelled during fetch/prefetch lookup
          if (gen !== playGenRef.current) { resolve(null); return; }

          const decoded = await ctx.decodeAudioData(raw);
          audioBufferCacheRef.current.set(src, decoded);

          // Check if cancelled during decode
          if (gen !== playGenRef.current) { resolve(null); return; }

          stopAudio();
          playGenRef.current = gen;  // restore our token after stopAudio incremented it
          setIsPlaying(true);
          const source = ctx.createBufferSource();
          source.buffer = decoded;
          source.connect(gainNodeRef.current ?? ctx.destination);
          source.onended = () => { if (gen === playGenRef.current) { setIsPlaying(false); onEnded(); } };
          source.start(0);
          audioSourceRef.current = source;
          resolve(decoded.duration);
        } catch (err) {
          console.error("[DignityAudio] playback error:", err, "src:", src);
          resolve(null);
          if (gen === playGenRef.current) { setIsPlaying(false); onEnded(); }
        }
      }).catch(err => {
        console.error("[DignityAudio] AudioContext resume error:", err);
        resolve(null);
        if (gen === playGenRef.current) { setIsPlaying(false); onEnded(); }
      });
    });
  }, [getAudioCtx, stopAudio]);

  /** Preload audio URLs into the ArrayBuffer cache (best-effort, silent failures). */
  const preloadUrls = useCallback((urls: string[]) => {
    urls.forEach(async (src) => {
      if (arrayBufferCacheRef.current.has(src)) return;
      try {
        const r = await fetch(src);
        if (!r.ok) return;
        const buf = await r.arrayBuffer();
        arrayBufferCacheRef.current.set(src, buf);
      } catch (_) { /* best-effort preload */ }
    });
  }, []);

  return {
    playAudioSrc,
    stopAudio,
    pauseAudio,
    resumeAudio,
    volume,
    setVolume,
    preloadUrls,
    isPlaying,
    isPaused,
    // Expose refs for advanced usage (e.g., homepage popup checks gainNode directly)
    gainNodeRef,
    audioCtxRef,
  };
}
